import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import {
    AddExamRequestWorkflow,
    AddExamsRequestWorkflow,
    AdmissionConfigurationWorkflowDTO,
    AdmissionRequestWorkflow,
    AdmissionWorkflowDTO,
    AgendaSearchWorkflow,
    AgendaWorkflowDTO,
    AnamnesticFieldSearchWorkflow,
    AnamnesticFieldWorkflowDTO,
    AuthData,
    BusinessUnitValidationRequestWorkflow,
    BusinessUnitValidationWorkflowDTO,
    DynamicFieldWorkflowDTO,
    EquipmentSearchWorkflow,
    EquipmentWorkflowDTO,
    ExaminationWorkflowDTO,
    ExamSearchWorkflow,
    ExamWorkflowDTO,
    ExecutionWorkflow,
    MandatoryEquipmentChangeWorkflow,
    ModifyExaminationRequestWorkflow,
    ModifyExamRequesthWorkflow,
    QuestionListSearchWorkflow,
    QuestionListWorkflowDTO,
    SendToWorklistWorkflow,
    SetTechnicianWorkflow,
    SuspensionWorkflow,
    UndoExecutionWorkflow,
    UndoSuspensionWorkflow,
} from '@se/models';
import { Observable } from 'rxjs';
import { CaseHistory, DeleteExaminations } from '../../../models/vnext.models';
import { ServerRootService } from '../../../utils/routing/serverroot.service';

/** Il servizio WorkflowBaseService deve contenere tutte le chiamate ai metodi rest nel controller WORKFLOW!
 * Ogni metodo non deve avere logica ma solo effettuare la chiamata.
 * il tipo di ritorno deve essere sempre un Observable
 */
export abstract class WorkflowBaseService extends ServerRootService {
    protected readonly httpClient = inject(HttpClient);

    sendToWorklist(data: SendToWorklistWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/SendToWorklist',
            data,
            this.buildOptions(authData),
        );
    }

    execute(data: ExecutionWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/Execute',
            data,
            this.buildOptions(authData),
        );
    }

    undoExecution(data: UndoExecutionWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/UndoExecution',
            data,
            this.buildOptions(authData),
        );
    }

    suspend(data: SuspensionWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/Suspend',
            data,
            this.buildOptions(authData),
        );
    }

    undoSuspension(data: UndoSuspensionWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/UndoSuspension',
            data,
            this.buildOptions(authData),
        );
    }

    setTechnician(data: SetTechnicianWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/SetTechnician',
            data,
            this.buildOptions(authData),
        );
    }

    validateBusinessUnit(
        data: BusinessUnitValidationRequestWorkflow,
        authData?: AuthData,
    ): Observable<BusinessUnitValidationWorkflowDTO> {
        return this.httpClient.post<BusinessUnitValidationWorkflowDTO>(
            this.getServerRoot() + '/Workflow/ValidateBusinessUnit',
            data,
            this.buildOptions(authData),
        );
    }

    getEquipments(data: EquipmentSearchWorkflow, authData?: AuthData): Observable<EquipmentWorkflowDTO[]> {
        return this.httpClient.post<EquipmentWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetEquipments',
            data,
            this.buildOptions(authData),
        );
    }

    getAgendas(data: AgendaSearchWorkflow, authData?: AuthData): Observable<AgendaWorkflowDTO[]> {
        return this.httpClient.post<AgendaWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetAgendas',
            data,
            this.buildOptions(authData),
        );
    }

    /**Cancella più examinations in stato A/E. */
    deleteExaminations(data: DeleteExaminations, authData?: AuthData): Observable<boolean> {
        return this.httpClient.post<boolean>(
            this.getServerRoot() + '/Workflow/DeleteExaminations',
            data,
            this.buildOptions(authData),
        );
    }

    getExams(data: ExamSearchWorkflow, authData?: AuthData): Observable<ExamWorkflowDTO[]> {
        return this.httpClient.post<ExamWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetExams',
            data,
            this.buildOptions(authData),
        );
    }

    insertAdmission(data: AdmissionRequestWorkflow, authData?: AuthData): Observable<AdmissionWorkflowDTO> {
        return this.httpClient.post<AdmissionWorkflowDTO>(
            this.getServerRoot() + '/Workflow/InsertAdmission',
            data,
            this.buildOptions(authData),
        );
    }

    changeEquipment(data: MandatoryEquipmentChangeWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/ChangeEquipment',
            data,
            this.buildOptions(authData),
        );
    }

    getDynamicFields(authData?: AuthData): Observable<DynamicFieldWorkflowDTO[]> {
        return this.httpClient.post<DynamicFieldWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetDynamicFields',
            this.buildOptions(authData),
        );
    }

    getAnamnesticFields(
        data: AnamnesticFieldSearchWorkflow,
        authData?: AuthData,
    ): Observable<AnamnesticFieldWorkflowDTO[]> {
        return this.httpClient.post<AnamnesticFieldWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetAnamnesticFields',
            data,
            this.buildOptions(authData),
        );
    }

    getCaseHistory(data: QuestionListSearchWorkflow, authData?: AuthData): Observable<CaseHistory> {
        const params = new HttpParams().set('examinationId', data.idExaminations[0]);
        const options = {
            ...this.buildOptions(authData),
            params,
        };
        return this.httpClient.get<CaseHistory>(
            this.getServerRoot() + '/MammographicScreening/GetCaseHistory',
            options,
        );
    }

    getCaseHistoryXml(data: QuestionListSearchWorkflow, authData?: AuthData): Observable<Blob> {
        const params = new HttpParams().set('examinationId', data.idExaminations[0]);

        const options = {
            ...this.buildOptions(authData),
            headers: new HttpHeaders({ Accept: 'application/pdf' }),
            params,
            responseType: 'blob' as 'json',
        };

        return this.httpClient.get<Blob>(this.getServerRoot() + '/MammographicScreening/GetCaseHistoryXml', options);
    }

    getQuestionsLists(data: QuestionListSearchWorkflow, authData?: AuthData): Observable<QuestionListWorkflowDTO[]> {
        const params = {'idExaminations': data.idExaminations };
        return this.httpClient.post<QuestionListWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetQuestionsLists',
            params,
            this.buildOptions(authData),
        );
    }

    getAdmissionConfigurations(
        idBusinessUnit: string,
        authData?: AuthData,
    ): Observable<AdmissionConfigurationWorkflowDTO[]> {
        return this.httpClient.get<AdmissionConfigurationWorkflowDTO[]>(
            this.getServerRoot() + '/Workflow/GetAdmissionConfigurations/?idBusinessUnit=' + idBusinessUnit,
            this.buildOptions(authData),
        );
    }

    modifyExam(data: ModifyExamRequesthWorkflow, authData?: AuthData): Observable<ExaminationWorkflowDTO> {
        return this.httpClient.post<ExaminationWorkflowDTO>(
            this.getServerRoot() + '/Workflow/ModifyExam',
            data,
            this.buildOptions(authData),
        );
    }

    modifyExamination(
        data: ModifyExaminationRequestWorkflow,
        authData?: AuthData,
    ): Observable<ExaminationWorkflowDTO> {
        return this.httpClient.post<ExaminationWorkflowDTO>(
            this.getServerRoot() + '/Workflow/ModifyExamination',
            data,
            this.buildOptions(authData),
        );
    }

    getExaminationDetails(idExamination: string, authData?: AuthData): Observable<ExaminationWorkflowDTO> {
        return this.httpClient.get<ExaminationWorkflowDTO>(
            this.getServerRoot() + '/Workflow/GetExaminationDetails/?idExamination=' + idExamination,
            this.buildOptions(authData),
        );
    }

    addExam(data: AddExamRequestWorkflow, authData?: AuthData): Observable<ExaminationWorkflowDTO> {
        return this.httpClient.post<ExaminationWorkflowDTO>(
            this.getServerRoot() + '/Workflow/AddExam',
            data,
            this.buildOptions(authData),
        );
    }

    addExams(data: AddExamsRequestWorkflow, authData?: AuthData): Observable<void> {
        return this.httpClient.post<void>(
            this.getServerRoot() + '/Workflow/AddExams',
            data,
            this.buildOptions(authData),
        );
    }

    getLastExamNumber(idBusinessUnit: string, authData?: AuthData): Observable<any> {
        return this.httpClient.get(
            this.getServerRoot() + '/Workflow/GetLastExamNumber?idBusinessUnit=' + idBusinessUnit,
            this.buildOptions(authData)
        );
    }

    modifyQuestionsList(questionsList: QuestionListWorkflowDTO, authData?: AuthData): Observable<any> {
        return this.httpClient.post<any>(
            this.getServerRoot() + '/Workflow/ModifyQuestionsList',
            questionsList,
            this.buildOptions(authData)
        );
    }
}
